if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.multi_item_request = false;
    }

    connectedCallback() {
      if (this._productFormInitialized) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      const idInput = this.form.querySelector('[name=id]');
      if (idInput) idInput.disabled = false;
      this._boundSubmitHandler = this.onSubmitHandler.bind(this);
      this.form.addEventListener('submit', this._boundSubmitHandler);
      this.submitButton = this.querySelector('[type="submit"]');
      this.cartNotification = document.getElementById('cart-notification');
      this.cartNotificationItem = document.getElementById('cart-notification-item');
      this._productFormInitialized = true;
    }

    disconnectedCallback() {
      if (this.form && this._boundSubmitHandler) {
        this.form.removeEventListener('submit', this._boundSubmitHandler);
      }
      this._productFormInitialized = false;
    }

    /**
     * Resolves product id for ajaxProduct:added from add response and existing DOM/form.
     * @param {Object} json - Cart add API response
     * @returns {string|number|undefined}
     */
    getProductIdForEvent(json) {
      if (json?.product_id != null) return json.product_id;
      if (json?.items?.[0]?.product_id != null) return json.items[0].product_id;
      const fromPicker = this.querySelector('[data-product-id]')?.dataset?.productId;
      if (fromPicker != null && fromPicker !== '') return fromPicker;
      const fromParent = this.closest('[data-product]')?.dataset?.product;
      if (fromParent != null && fromParent !== '') return fromParent;
      const formId = this.form?.id ?? '';
      if (/^product-form-\d+$/.test(formId)) return formId.replace(/^product-form-/, '');
      if (formId.includes('-upsell-')) return formId.split('-upsell-')[1] ?? '';
      if (json?.id != null) return json.id;
      return undefined;
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay')?.classList.remove('hidden');

      // Upsell items handled only in JS
      const upsell_ids_nodes = document.querySelectorAll(`[data-form="${this.form.getAttribute('id')}"] .upsell-id`);
      const upsell_quantities_nodes = document.querySelectorAll(`[data-form="${this.form.getAttribute('id')}"] .upsell-quantity`);
      let upsell_items = [];

      if (upsell_ids_nodes.length !== 0) {
        const upsell_ids = Array.from(upsell_ids_nodes).map(input => parseInt(input.value));
        const upsell_quantities = Array.from(upsell_quantities_nodes).map(input => parseInt(input.value));
        upsell_items = upsell_ids.map((id, index) => ({'id': id,'quantity': upsell_quantities[index]})).filter(item => item.quantity !== 0);
      }

      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
        }
      }
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const formData = new FormData(this.form);

      // If upsell items added refactor FormData
      if (upsell_items.length > 0) {
        this.multi_item_request = true;
        const items = upsell_items;
        const id = parseInt(formData.get('id'));
        const quantity = parseInt(formData.get('quantity')) || 1;
        const the_product_item = { 'id': id, 'quantity': quantity }

        items.push(the_product_item);
        const itemsData = { 'items': items };

        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify(itemsData);
      } else {
        this.multi_item_request = false;
        config.body = formData;
      }

      const queue = window.YBTCOCartMutationQueue || ((operation) => operation());
      try {
        const json = await queue(async () => {
          let res;
          try {
            res = await fetch(routes.cart_add_url + '.js', config);
            const responseJson = await res.json();
            if (!res.ok) {
              const error = new Error(responseJson?.description || responseJson?.message || `Cart add failed (${res.status})`);
              error.knownResponse = res.status >= 400 && res.status < 500;
              error.uncertain = res.status >= 500;
              throw error;
            }
            return responseJson;
          } catch (error) {
            // A network failure leaves the POST outcome unknown. The caller
            // reconciles via GET instead of retrying and risking a duplicate add.
            if (!error.knownResponse) error.uncertain = true;
            throw error;
          }
        });

        if (this.cartNotification) {
          try {
            const notificationTemplate = document.importNode(this.cartNotification.content, true);

            const titleElement = notificationTemplate.querySelector('[data-notification-title]');
            const itemsElement = notificationTemplate.querySelector('[data-items]');
            if (this.multi_item_request) {
              if (titleElement) titleElement.textContent = window.variantStrings.itemsAdded;
              const items = Array.isArray(json?.items) ? [...json.items] : [];
              const lastElement = items.pop();
              if (lastElement) items.unshift(lastElement);

              items.forEach(item => {
                const singleItem = this.createNotificationItem(item);
                if (singleItem) itemsElement?.appendChild(singleItem);
              });
            } else {
              if (titleElement) titleElement.textContent = window.variantStrings.itemAdded;
              const singleItem = this.createNotificationItem(json);
              if (singleItem) itemsElement?.appendChild(singleItem);
            }

            if (document.querySelector('.cart-notification')) {
              document.querySelector('.cart-notification').remove();
            }

            document.body.appendChild(notificationTemplate);

            const the_notification = document.querySelector('.cart-notification');

            // A malformed optional notification should never turn a successful
            // cart add into an apparent failed add or invite a duplicate retry.
            if (the_notification) {
              the_notification.setAttribute('tabindex', '-1');
              the_notification.focus();

              the_notification.querySelector('.notification-close')?.addEventListener('click', () => {
                the_notification.classList.remove('anim-in');
              });
              setTimeout(() => {
                the_notification.classList.add('anim-in');
              }, 50);
            }
          } catch (notificationError) {
            console.error('Cart notification failed after successful add:', notificationError);
          }
        }

        const cartStatus = document.getElementById('cart-live-region-text');
        if (cartStatus) {
          cartStatus.textContent = window.cartStrings?.added || window.cartStrings?.updated || 'Added to cart';
          cartStatus.setAttribute('aria-hidden', false);
          setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
        }

        const productId = this.getProductIdForEvent(json);
        const event = new CustomEvent('ajaxProduct:added', { detail: productId != null ? { productId } : {} });
        document.dispatchEvent(event);
      } catch(error) {
        if (error?.uncertain) {
          try {
            const reconciledCart = queue.reconcile ? await queue.reconcile() : null;
            await document.querySelector('cart-items')?.renderItems?.();
            if (reconciledCart) document.dispatchEvent(new CustomEvent('cart:reconciled', { detail: reconciledCart }));
          } catch (reconcileError) {
            console.error(reconcileError);
          }
        }
        const message = error?.knownResponse
          ? error.message
          : (window.cartStrings?.error || error?.message || error);
        this.handleErrorMessage(message);
        console.error(error);
      }

      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-disabled');
      this.querySelector('.loading-overlay')?.classList.add('hidden');
      this.resetQuantityInput();
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper') || document.querySelector(`.product-form__error-message-wrapper[form="${this.form.getAttribute('id')}"]`)
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    resetQuantityInput() {
      const hidden_quantity_input = document.querySelector('[type=hidden][name=quantity]');
      if (hidden_quantity_input) {
        hidden_quantity_input.value = 1;
      }
    }

    createNotificationItem(product) {
      if (!this.cartNotificationItem || !product) return null;
      const notificationItemTemplate = document.importNode(this.cartNotificationItem.content, true);
      const imageElement = notificationItemTemplate.querySelector('.notification-item-image');
      const detailsElement = notificationItemTemplate.querySelector('.notification-item-details');
      const titleElement = notificationItemTemplate.querySelector('.notification-item-title');
      const filename = product.image?.split('/').pop();
      if (filename) {
        const lastDotIndex = filename.lastIndexOf('.');
        const newFilename = lastDotIndex > 0
          ? `${filename.substring(0, lastDotIndex)}_120x${filename.substring(lastDotIndex)}`
          : filename;
        imageElement?.setAttribute('src', product.image.replace(filename, newFilename));
        imageElement?.classList.remove('hide');
      } else {
        imageElement?.classList.add('hide');
      }
      if (titleElement) titleElement.textContent = product.product_title || '';

      (product.options_with_values || []).forEach(item => {
        const row = document.createElement("dl");
        const nameCell = document.createElement("dt");
        const valueCell = document.createElement("dd");

        nameCell.textContent = item.name + ': ';
        valueCell.textContent = item.value;

        row.appendChild(nameCell);
        row.appendChild(valueCell);
        detailsElement?.appendChild(row);
      });

      const propertyKeyValues = Object.entries(product.properties || {});
      if (propertyKeyValues.length > 0) {
        const properties = document.createElement("dl");
        propertyKeyValues.forEach(([key, value]) => {
          const startsWith = key.startsWith('_');
          if (startsWith) return;
          const row = document.createElement("dl");
          const nameCell = document.createElement("dt");
          const valueCell = document.createElement("dd");

          nameCell.textContent = key + ': ';
          valueCell.textContent = value;

          row.appendChild(nameCell);
          row.appendChild(valueCell);
          properties.appendChild(row);
        });
        detailsElement?.appendChild(properties);
      }

      return notificationItemTemplate;
    }
  });
}

class AddMultiplier extends HTMLElement {
  constructor() {
    super();

    const multiplyButton = this.querySelector('button');
    const multipliedQuantity = this.querySelector('[name=multiplied-quantity]');
    const productFormElement = this.closest('product-form');
    if (!multiplyButton || !multipliedQuantity || !productFormElement) return;
    this.submitEvent = new SubmitEvent("submit", { submitter: multiplyButton });
    this.multiplyBy = parseInt(multipliedQuantity.value, 10) || 1;
    this.productForm = productFormElement.querySelector('form');
    if (!this.productForm) return;
    const formId = this.productForm.getAttribute('id');
    this.quantity_input = document.querySelector(`[name=quantity][form=${ formId }]`);
    this.submitProductFormHandler = this.submitProductForm.bind(this);
    multiplyButton.addEventListener('click', this.submitProductFormHandler);
  }

  submitProductForm() {
    if (!this.quantity_input || !this.productForm) return;
    this.quantity_input.value = this.multiplyBy;
    this.productForm.dispatchEvent(this.submitEvent);
  }
}
customElements.define("add-multiplier", AddMultiplier);
